import React, { useState, useEffect, useRef } from 'react';
import { X, Mail } from 'lucide-react';
import { ANIMATION, URLS, ANALYTICS_EVENTS } from '../constants';
import AnimatedSlider from './AnimatedSlider';

interface InstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InstructionsModal: React.FC<InstructionsModalProps> = ({
  isOpen,
  onClose,
}) => {
  // Animation state
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Card flip animation states
  const [isFlipping, setIsFlipping] = useState(false);
  const [showDigit, setShowDigit] = useState(true);

  // Toast state for email copy
  const [showToast, setShowToast] = useState(false);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle modal animations
  useEffect(() => {
    if (isOpen) {
      // First render the component
      setShouldRender(true);
      // Then start the animation using nested requestAnimationFrame for reliability
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      // First run the exit animation
      setIsVisible(false);
      // Then remove from DOM after animation completes
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, ANIMATION.STANDARD_DELAY); // Match this duration to CSS transition
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Set up automatic flip animation every few seconds
  useEffect(() => {
    if (!isOpen) return;

    // Start with an initial delay
    const initialDelay = setTimeout(() => {
      startFlipAnimation();
    }, ANIMATION.RETRY_DELAY);

    // Set up interval for repeating animation
    const flipInterval = setInterval(() => {
      startFlipAnimation();
    }, ANIMATION.INSTRUCTIONS_FLIP); // Flip every 4 seconds

    function startFlipAnimation() {
      // Hide the digit and start flip animation
      setShowDigit(false);
      setIsFlipping(true);

      // After half the animation (when card is edge-on), change to revealed state
      setTimeout(() => {
        setIsFlipping(false);
        // After the flip animation is complete, show the digit
        setTimeout(() => {
          setShowDigit(true);
        }, 50);
      }, 500);
    }

    // Clean up on unmount or when modal closes
    return () => {
      clearTimeout(initialDelay);
      clearInterval(flipInterval);
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, [isOpen]);

  const handleEmailClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    // Track email click
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', ANALYTICS_EVENTS.SOCIAL_CLICK, {
        social_platform: 'email',
        event_category: ANALYTICS_EVENTS.SOCIAL_ENGAGEMENT,
      });
    }

    try {
      await navigator.clipboard.writeText('whichyrgame@gmail.com');
      setShowToast(true);

      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }

      toastTimeoutRef.current = setTimeout(() => {
        setShowToast(false);
      }, 2000);
    } catch (err) {
      // Fallback: open email client if clipboard fails
      window.location.href = 'mailto:whichyrgame@gmail.com';
    }
  };

  const handleSocialClick = (platform: string) => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', ANALYTICS_EVENTS.SOCIAL_CLICK, {
        social_platform: platform,
        event_category: ANALYTICS_EVENTS.SOCIAL_ENGAGEMENT,
      });
    }
  };

  // Don't render if shouldRender is false
  if (!shouldRender) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:p-4 overflow-hidden"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop with animation */}
      <div
        className={`absolute inset-0 bg-black/70 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      ></div>

      {/* Modal Content with animations */}
      <div
        className={`w-full bg-[#0D0713] relative rounded-t-lg rounded-b-none md:rounded-lg md:max-w-md flex flex-col max-h-[90vh] md:max-h-[95vh] transition-all duration-300 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-[#0D0713] px-6 pt-6 pb-3 border-b border-white/20">
          <div className="flex items-center justify-between">
            <h2 className="text-white font-black uppercase leading-none">
              <span>WHICH</span>{' '}
              <span className="text-[#C6A4FF] ml-[-2px]">YEAR</span>
            </h2>

            <button
              onClick={onClose}
              className="text-white transition-transform hover:scale-110 active:scale-95"
            >
              <X size={24} />
            </button>
          </div>
          <div className="flex items-center self-start">
            <h2 className="text-white font-bold text-xl">How to Play</h2>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-6 pb-6 text-white">
          <ul
            className={`space-y-8 transition-opacity duration-300 ease-in-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          >
            <li className="flex flex-col text-left gap-3">
              <div className="flex items-center gap-4">
                <span className="leading-[130%] text-[16px]">
                  Guess which year each photo was taken.
                </span>
              </div>
            </li>

            <li className="flex flex-col text-left gap-3">
              {/* Animated Slider Component - Replaces the static visualization */}
              <AnimatedSlider />
              <div className="flex items-center gap-4">
                <span className="leading-[130%] text-[16px]">
                  The closer your guess, the more points you earn!
                </span>
              </div>
            </li>

            <li className="flex flex-col text-left items-center gap-3">
              <div className="flex items-center justify-start w-full py-2 px-4 bg-[#1A1321] rounded-lg space-x-2">
                <div className="w-10 h-12 flex items-center justify-center rounded text-lg bg-[#333333]/30 border border-[#333333] text-white bg-white">
                  9
                </div>
                <div className="w-10 h-12 flex items-center justify-center rounded text-lg bg-[#333333]/30 border border-[#333333] text-white bg-white ">
                  9
                </div>
                <div className="w-10 h-12 flex items-center justify-center rounded text-lg bg-[#333333]/30 border border-[#333333] text-white bg-white">
                  9
                </div>
                <div
                  className={`w-10 h-12 flex items-center justify-center rounded text-lg border border-[#333333] font-bold
                    ${isFlipping ? 'flip-card' : 'bg-[#444444]/50 text-white/70'}`}
                >
                  {showDigit ? '1' : ''}
                </div>
              </div>
              <div className="flex items-center justify-start w-full space-x-2">
                <div className="flex items-center gap-4">
                  <span className="leading-[130%] text-[16px]">
                    Use <span className="font-bold">digit reveals</span> to
                    unlock one digit totally free, no point penalty. Each digit
                    can only be revealed once per game.
                  </span>
                </div>
              </div>
            </li>
          </ul>

          {/* Social Links Section */}
          <div className="mt-8 pt-6 border-t border-white/10">
            <p className="text-white/60 text-xs text-center mb-3">
              Feedback, questions, or photo suggestions:
            </p>
            <div className="flex items-center justify-center gap-4">
              <button
                className="text-white/60 hover:text-white transition-colors"
                title="Copy email"
                onClick={handleEmailClick}
              >
                <Mail size={20} />
              </button>
              <a
                href={URLS.TWITTER}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white transition-colors"
                title="Follow on X (Twitter)"
                onClick={() => handleSocialClick('X')}
              >
                {/* X (Twitter) Icon */}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a
                href={URLS.BLUESKY}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white transition-colors"
                title="Follow on Bluesky"
                onClick={() => handleSocialClick('Bluesky')}
              >
                {/* Bluesky Icon */}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" />
                </svg>
              </a>
              {/*
              <a
                href={URLS.SUPPORT}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white transition-colors"
                title="Buy me a coffee"
                onClick={() => handleSocialClick('BuyMeACoffee')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 8h1a4 4 0 1 1 0 8h-1"/>
                  <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/>
                  <line x1="6" y1="2" x2="6" y2="4"/>
                  <line x1="10" y1="2" x2="10" y2="4"/>
                  <line x1="14" y1="2" x2="14" y2="4"/>
                </svg>
              </a>
              */}
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="sticky bottom-0 z-10 bg-[#0D0713] px-6 py-4 border-t border-white/20">
          {/* Close Button */}
          <div className="flex justify-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-[#7236DC] hover:bg-[#8347ED] transition-colors rounded-md text-white font-bold"
            >
              Got it!
            </button>
          </div>
        </div>
      </div>

      {/* Toast notification */}
      {showToast && (
        <div
          className="fixed top-8 left-1/2 transform -translate-x-1/2 bg-white text-[#090312] px-4 py-2 rounded-lg shadow-lg z-[60] animate-toast-in font-bold"
          style={{
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          Copied email to clipboard!
        </div>
      )}
    </div>
  );
};

export default InstructionsModal;
